import { Loader2, Sparkles } from "lucide-react";

/**
 * Live progress UI for async tool jobs.
 * Pass `tick = { elapsedSec, etaSec }` from runToolJob's onTick callback.
 * Displays a smooth pseudo-progress bar (asymptotic) so the user never thinks the app froze.
 */
export default function JobProgress({ tick, label = "Analyse läuft…" }) {
  if (!tick) return null;

  const { elapsedSec, etaSec = 60 } = tick;
  // Asymptotic progress: never reaches 100, slows down past ETA.
  const ratio = elapsedSec / etaSec;
  const pct = Math.min(96, ratio < 1 ? ratio * 80 : 80 + (1 - Math.exp(-(ratio - 1))) * 16);

  const etaRemaining = Math.max(0, etaSec - elapsedSec);
  const hint = elapsedSec > etaSec
    ? "Letzter Feinschliff — die KI verdichtet gerade die Insights…"
    : `Geschätzt noch ~${etaRemaining}s`;

  // After 30s show the patient AI message — reassures the user the wait is worth it.
  const showPatienceMsg = elapsedSec >= 30;

  return (
    <div className="p-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-lime-400/5 border border-cyan-500/30" data-testid="job-progress">
      <div className="flex items-center gap-3 mb-3">
        <Loader2 className="w-5 h-5 text-cyan-400 animate-spin flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">{label}</p>
          <p className="text-[11px] text-zinc-400">{hint} · {elapsedSec}s vergangen</p>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-lime-400 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {showPatienceMsg ? (
        <div
          className="mt-4 p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/20 flex items-start gap-2.5"
          data-testid="job-patience-msg"
        >
          <Sparkles className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-cyan-200 mb-0.5">Die KI baut gerade dein perfektes Konzept</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Die Wartezeit ist heute etwas länger, weil im Hintergrund die KI dein perfektes Konzept zusammenstellt.
              Nimm dir kurz Zeit und sei nicht ungeduldig — es lohnt sich. Du kannst den Tab geöffnet lassen,
              die Analyse läuft im Hintergrund weiter.
            </p>
          </div>
        </div>
      ) : (
        <p className="text-[10px] text-zinc-500 mt-2">
          Wir scrapen via Apify + GPT-5.2 — kein Tab schließen, läuft im Hintergrund weiter.
        </p>
      )}
    </div>
  );
}
